import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Copy, Check, ExternalLink, Filter } from "lucide-react";
import Navbar from "@/components/Navbar";
import {
  getBaseOverview,
  getBaseAssets,
  getBaseLeaderboard,
  getBaseHype,
  getBaseCallers,
  getBaseMetrics,
  shortContract,
  explorerAddressUrl,
} from "@/lib/baseApi";
import { fmtUSD } from "@/lib/format";
import { switchEvmChain, identifyInjectedWallets } from "@/lib/onchain";
import { toast } from "sonner";
import ClassificationBadges from "@/components/ClassificationBadges";

const ASSET_FILTERS = [
  { key: "", label: "All" },
  { key: "crypto_token", label: "Crypto" },
  { key: "meme_token", label: "Meme" },
  { key: "rwa", label: "RWA" },
  { key: "tokenized_equity", label: "Equity" },
  { key: "tokenized_etf", label: "ETF" },
  { key: "stablecoin", label: "Stable" },
  { key: "unknown", label: "Unknown" },
];

const MARKET_SORTS = [
  { key: "newest", label: "Newest" },
  { key: "volume", label: "24h volume" },
  { key: "liquidity", label: "Liquidity" },
  { key: "momentum", label: "Momentum" },
];

function CopyAddr({ address }) {
  const [ok, setOk] = useState(false);
  if (!address) return null;
  return (
    <button
      type="button"
      data-testid="base-copy-address"
      className="inline-flex items-center gap-1 font-mono text-[11px] text-zinc-400 hover:text-white"
      onClick={() => {
        navigator.clipboard.writeText(address);
        setOk(true);
        setTimeout(() => setOk(false), 1200);
      }}
    >
      {shortContract(address)}
      {ok ? <Check className="h-3 w-3" /> : <Copy className="h-3 w-3" />}
    </button>
  );
}

function Stat({ label, value, testId }) {
  if (value == null || value === "") return null;
  return (
    <div data-testid={testId} className="min-w-[7rem]">
      <div className="text-[10px] uppercase tracking-[0.18em] text-zinc-500">{label}</div>
      <div className="mt-1 font-display text-2xl font-bold tabular-nums text-white">{value}</div>
    </div>
  );
}

function AssetRow({ a, mode }) {
  const href = a.slug ? `/token/${a.slug}` : null;
  const inner = (
    <>
      <div className="flex min-w-0 items-center gap-3">
        {a.logo ? (
          <img src={a.logo} alt="" className="h-8 w-8 rounded-full bg-white/5 object-cover" />
        ) : (
          <div className="flex h-8 w-8 items-center justify-center rounded-full bg-white/5 font-mono text-[10px] text-zinc-500">
            {(String(a?.ticker || a?.symbol || "?")).slice(0, 3)}
          </div>
        )}
        <div className="min-w-0">
          <div className="truncate font-display font-bold">{a.ticker || a.symbol || "—"}</div>
          <div className="truncate text-xs text-zinc-500">{a.name || "Unnamed"}</div>
          <ClassificationBadges entity={a} testId={`base-badges-${a.contract_address || a.slug || "row"}`} />
        </div>
      </div>
      <div className="hidden flex-col items-end sm:flex">
        <div className="text-[10px] uppercase tracking-wider text-zinc-600">Base · 8453</div>
        <CopyAddr address={a.contract_address} />
      </div>
      {mode === "backing" ? (
        <div className="text-right font-mono text-sm text-emerald-300">
          {fmtUSD(a.backing_total_usd ?? a.total_backed_usd)}
          <div className="text-[10px] uppercase tracking-wider text-zinc-600">
            Rank {a.filter_rank || a.backing_rank || a.rank || "—"}
          </div>
        </div>
      ) : mode === "hype" ? (
        <div className="text-right font-mono text-sm text-amber-200">
          {a.hype_score ?? a.score ?? "—"}
          <div className="text-[10px] uppercase tracking-wider text-zinc-600">Hype</div>
        </div>
      ) : (
        <div className="text-right font-mono text-sm text-zinc-300">
          {a.market_volume_24h_usd != null ? fmtUSD(a.market_volume_24h_usd) : "—"}
          <div className="text-[10px] uppercase tracking-wider text-zinc-600">
            {a.market_stale ? "Stale vol" : "Ext. 24h vol"}
          </div>
        </div>
      )}
    </>
  );
  const cls =
    "grid grid-cols-[1fr_auto] items-center gap-3 border-b border-white/[0.06] px-3 py-3 transition-colors hover:bg-white/[0.03] sm:grid-cols-[1.2fr_1fr_auto]";
  if (href) {
    return (
      <Link to={href} data-testid={`base-asset-${a.contract_address || a.slug}`} className={cls}>
        {inner}
      </Link>
    );
  }
  return <div className={cls}>{inner}</div>;
}

export default function BaseEcosystemPage() {
  const [overview, setOverview] = useState(null);
  const [metrics, setMetrics] = useState(null);
  const [board, setBoard] = useState([]);
  const [assets, setAssets] = useState([]);
  const [hype, setHype] = useState([]);
  const [callers, setCallers] = useState([]);
  const [assetType, setAssetType] = useState("");
  const [marketSort, setMarketSort] = useState("newest");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [walletHint, setWalletHint] = useState({ available: false, label: "" });

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [ov, met, lb, as, hy, ca] = await Promise.all([
        getBaseOverview(),
        getBaseMetrics().catch(() => null),
        getBaseLeaderboard({ limit: 25 }),
        getBaseAssets({
          limit: 40,
          sort: marketSort,
          ...(assetType ? { asset_type: assetType } : {}),
        }),
        getBaseHype({ limit: 25 }),
        getBaseCallers({ limit: 25 }),
      ]);
      setOverview(ov);
      setMetrics(met);
      setBoard(lb?.entities || []);
      setAssets(as?.assets || []);
      setHype((hy?.tokens || []).map((t) => ({
        ...(t.entity || {
          id: t.contract_address,
          ticker: t.symbol,
          name: t.name,
          contract_address: t.contract_address,
          chain: t.chain,
          backing_total_usd: t.backing_total_usd,
        }),
        hype_score: t.hype_score,
        components: t.components,
      })));
      setCallers(ca?.callers || []);
    } catch (e) {
      setError(e?.response?.data?.detail || e?.message || "Failed to load Base ecosystem");
    } finally {
      setLoading(false);
    }
  }, [assetType, marketSort]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    const w = identifyInjectedWallets();
    const label = w.coinbase ? "Coinbase Wallet" : w.metamask ? "MetaMask" : w.hasEvm ? "Injected wallet" : "";
    setWalletHint({ available: Boolean(w.metamask || w.coinbase || w.hasEvm), label });
  }, []);

  const rwaAssets = useMemo(
    () => assets.filter((a) => ["rwa", "tokenized_equity", "tokenized_etf"].includes(a.asset_type)),
    [assets],
  );

  const onSwitchNetwork = async () => {
    try {
      await switchEvmChain("base");
      toast.success("Switched to Base (8453)");
    } catch (e) {
      toast.error(e?.message?.slice(0, 100) || "Network switch failed");
    }
  };

  return (
    <div className="min-h-screen bg-[#050505] text-white" data-testid="base-ecosystem-page">
      <Navbar category="tokens" />

      {/* Hero — brand first, one CTA group, no card clutter */}
      <section className="relative overflow-hidden border-b border-white/10">
        <div
          aria-hidden
          className="pointer-events-none absolute inset-0"
          style={{
            background:
              "radial-gradient(ellipse 80% 60% at 15% 20%, rgba(16,185,129,0.18), transparent 55%), "
              + "radial-gradient(ellipse 50% 40% at 85% 10%, rgba(251,191,36,0.08), transparent 50%), "
              + "linear-gradient(180deg, #0a1210 0%, #050505 70%)",
          }}
        />
        <div className="relative mx-auto max-w-6xl px-4 pb-12 pt-10 sm:px-6 sm:pt-14">
          <motion.p
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.45 }}
            className="font-display text-sm font-bold uppercase tracking-[0.28em] text-emerald-400/90"
          >
            BackOnFlip
          </motion.p>
          <motion.h1
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.55, delay: 0.05 }}
            className="mt-3 max-w-3xl font-display text-4xl font-black leading-[1.05] tracking-tight sm:text-6xl"
            data-testid="base-hero-title"
          >
            Base
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.12 }}
            className="mt-4 max-w-xl text-base text-zinc-400 sm:text-lg"
          >
            Discover what capital backs. See who called it first. Track who keeps being right.
          </motion.p>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.5, delay: 0.2 }}
            className="mt-7 flex flex-wrap items-center gap-3"
          >
            <button
              type="button"
              data-testid="base-switch-network"
              onClick={onSwitchNetwork}
              className="rounded-full bg-emerald-400 px-5 py-2.5 text-sm font-bold text-black transition hover:bg-emerald-300"
            >
              Use Base (8453)
            </button>
            <Link
              to="/?chain=base"
              className="rounded-full border border-white/20 px-5 py-2.5 text-sm font-bold text-white transition hover:bg-white/5"
            >
              Board filter
            </Link>
            {walletHint.available && (
              <span className="text-xs text-emerald-300/80" data-testid="base-wallet-detected">
                {walletHint.label || "Wallet"} detected
              </span>
            )}
          </motion.div>
          <div className="mt-10 flex flex-wrap gap-8 border-t border-white/10 pt-6">
            <Stat
              label="Discovered assets"
              value={overview?.stats?.discovered_assets ?? metrics?.assets_discovered}
              testId="base-stat-discovered"
            />
            <Stat
              label="BackOnFlip ranked"
              value={overview?.stats?.backonflip_ranked_assets ?? metrics?.backonflip_ranked_assets}
              testId="base-stat-ranked"
            />
            <Stat
              label="Market activity"
              value={overview?.stats?.market_activity_assets}
              testId="base-stat-market"
            />
            <Stat
              label="Active callers"
              value={metrics?.base_caller_count}
              testId="base-stat-callers"
            />
          </div>
        </div>
      </section>

      <main className="mx-auto max-w-6xl space-y-14 px-4 py-10 sm:px-6">
        {error && (
          <div data-testid="base-error" className="border border-red-500/30 bg-red-500/10 px-4 py-3 text-sm text-red-200">
            {error}
          </div>
        )}
        {loading && (
          <div data-testid="base-loading" className="text-sm text-zinc-500">Loading Base ecosystem…</div>
        )}

        {/* Separation banner */}
        <div className="grid gap-3 sm:grid-cols-2" data-testid="base-rank-vs-market">
          <div className="border-l-2 border-emerald-400 pl-4">
            <div className="text-[10px] uppercase tracking-[0.2em] text-emerald-400">BackOnFlip Rank</div>
            <p className="mt-1 text-sm text-zinc-400">Verified Backing only. External volume cannot change this order.</p>
          </div>
          <div className="border-l-2 border-amber-300/80 pl-4">
            <div className="text-[10px] uppercase tracking-[0.2em] text-amber-200">Market Activity</div>
            <p className="mt-1 text-sm text-zinc-400">External on-chain market data for discovery and Hype Radar.</p>
          </div>
        </div>

        {/* 1. BackOnFlip Leaderboard */}
        <section data-testid="base-section-leaderboard">
          <h2 className="font-display text-2xl font-bold">BackOnFlip Leaderboard</h2>
          <p className="mt-1 text-sm text-zinc-500">Chain filter · sort (−total_backed_usd, entity_id)</p>
          <div className="mt-4 border-t border-white/10">
            {!loading && board.length === 0 && (
              <p className="py-8 text-sm text-zinc-500" data-testid="base-board-empty">
                No Base entities with verified Backing yet.
              </p>
            )}
            {board.map((a) => (
              <AssetRow key={a.id || a.contract_address} a={a} mode="backing" />
            ))}
          </div>
        </section>

        {/* 2. Hype Radar */}
        <section data-testid="base-section-hype">
          <h2 className="font-display text-2xl font-bold">Hype Radar</h2>
          <p className="mt-1 text-sm text-zinc-500">
            Distinct from primary rank · market velocity + backing velocity + callers + freshness
          </p>
          <div className="mt-4 border-t border-white/10">
            {!loading && hype.length === 0 && (
              <p className="py-8 text-sm text-zinc-500" data-testid="base-hype-empty">No hype signals yet.</p>
            )}
            {hype.map((a) => (
              <AssetRow key={`h-${a.contract_address || a.id}`} a={a} mode="hype" />
            ))}
          </div>
        </section>

        {/* 3. New / market assets */}
        <section data-testid="base-section-assets">
          <div className="flex flex-wrap items-end justify-between gap-3">
            <div>
              <h2 className="font-display text-2xl font-bold">New Assets</h2>
              <p className="mt-1 text-sm text-zinc-500">Market Activity · discovery metadata only</p>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <Filter className="h-3.5 w-3.5 text-zinc-500" />
              {ASSET_FILTERS.map((f) => (
                <button
                  key={f.key || "all"}
                  type="button"
                  data-testid={`base-filter-${f.key || "all"}`}
                  onClick={() => setAssetType(f.key)}
                  className={`px-2.5 py-1 text-xs font-bold uppercase tracking-wide ${
                    assetType === f.key ? "text-emerald-300" : "text-zinc-500 hover:text-white"
                  }`}
                >
                  {f.label}
                </button>
              ))}
            </div>
          </div>
          <div className="mt-3 flex flex-wrap gap-2">
            {MARKET_SORTS.map((s) => (
              <button
                key={s.key}
                type="button"
                data-testid={`base-sort-${s.key}`}
                onClick={() => setMarketSort(s.key)}
                className={`border px-3 py-1 text-xs ${
                  marketSort === s.key
                    ? "border-amber-300/40 text-amber-100"
                    : "border-white/10 text-zinc-500"
                }`}
              >
                {s.label}
              </button>
            ))}
          </div>
          <div className="mt-4 border-t border-white/10">
            {!loading && assets.length === 0 && (
              <p className="py-8 text-sm text-zinc-500" data-testid="base-assets-empty">
                No discovered Base assets yet. Staging ingest may be idle.
              </p>
            )}
            {assets.map((a) => (
              <AssetRow key={`m-${a.contract_address || a.id}`} a={a} mode="market" />
            ))}
          </div>
        </section>

        {/* 4. RWA */}
        <section data-testid="base-section-rwa">
          <h2 className="font-display text-2xl font-bold">RWA / Tokenized Stocks</h2>
          <p className="mt-1 text-sm text-zinc-500">
            Informational only · underlying prices never count as Backing
          </p>
          <div className="mt-4 border-t border-white/10">
            {rwaAssets.length === 0 && (
              <p className="py-8 text-sm text-zinc-500" data-testid="base-rwa-empty">
                No source-backed RWA classifications yet.
              </p>
            )}
            {rwaAssets.map((a) => (
              <div key={`rwa-${a.contract_address}`} className="border-b border-white/[0.06] px-3 py-4">
                <AssetRow a={a} mode="market" />
                {a.underlying_asset && (
                  <div className="mt-2 pl-11 text-xs text-zinc-500">
                    Underlying: {a.underlying_asset.underlying_ticker || a.underlying_asset.underlying_name || "—"}
                    {a.underlying_asset.issuer ? ` · Issuer ${a.underlying_asset.issuer}` : ""}
                  </div>
                )}
              </div>
            ))}
          </div>
        </section>

        {/* 5. Callers */}
        <section data-testid="base-section-callers">
          <h2 className="font-display text-2xl font-bold">Top Callers</h2>
          <p className="mt-1 text-sm text-zinc-500">
            Global reputation + Base ecosystem breakdown
          </p>
          <div className="mt-4 border-t border-white/10">
            {callers.length === 0 && (
              <p className="py-8 text-sm text-zinc-500" data-testid="base-callers-empty">
                No Base caller activity recorded yet.
              </p>
            )}
            {callers.map((c) => (
              <div
                key={c.caller_id}
                data-testid={`base-caller-${c.caller_id}`}
                className="flex flex-wrap items-center justify-between gap-3 border-b border-white/[0.06] px-3 py-3"
              >
                <div>
                  <div className="font-display font-bold">{c.handle || c.slug || c.caller_id}</div>
                  <div className="text-xs text-zinc-500">
                    Global reputation: {c.global_reputation ?? "—"}
                  </div>
                </div>
                <div className="font-mono text-xs text-zinc-400">
                  Base {c.base?.calls ?? 0} calls · {c.base?.won ?? 0} won · {c.base?.open ?? 0} open
                  {c.base?.win_rate != null && (
                    <span className="ml-2 text-emerald-300/80">
                      {(c.base.win_rate * 100).toFixed(0)}% win
                    </span>
                  )}
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* Pitch strip */}
        <section data-testid="base-pitch" className="border-t border-white/10 pt-10 pb-16">
          <h2 className="font-display text-3xl font-black tracking-tight">
            BackOnFlip for Base
          </h2>
          <p className="mt-3 max-w-2xl text-zinc-400">
            Economic leaderboard, caller reputation, conviction calls, token and RWA discovery,
            and Hype Radar — built so the Base ecosystem can evaluate BackOnFlip as
            infrastructure before any $FLIP mint-chain decision.
          </p>
          <ul className="mt-6 space-y-2 text-sm text-zinc-500">
            <li>· Primary rank = verified Backing only</li>
            <li>· Market data powers discovery — never rank</li>
            <li>· No token-sale language · no partnership claimed</li>
          </ul>
          {overview?.network?.explorer_address_base && (
            <a
              href="https://basescan.org"
              target="_blank"
              rel="noreferrer"
              className="mt-6 inline-flex items-center gap-1 text-sm text-emerald-300 hover:underline"
            >
              Explorer <ExternalLink className="h-3.5 w-3.5" />
            </a>
          )}
        </section>
      </main>
    </div>
  );
}
